import React, { useState } from "react";
import { Order } from "../../services/orderService";
import { adminOrderService } from "../../services/admin/orderService";

interface OrderStatusUpdateProps {
  order: Order;
  onStatusUpdate: () => void;
}

const OrderStatusUpdate: React.FC<OrderStatusUpdateProps> = ({
  order,
  onStatusUpdate,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<Order["status"]>(
    order.status
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get valid next statuses based on current status
  const getValidNextStatuses = (currentStatus: Order["status"]): Order["status"][] => {
    const validTransitions: Record<Order["status"], Order["status"][]> = {
      new: ["confirmed", "canceled"],
      confirmed: ["new", "preparing"], // Allow unconfirm (back to new)
      preparing: ["shipped", "canceled"],
      shipped: [], // Final state
      canceled: ["new"], // Allow undo cancel (back to new)
    };
    return validTransitions[currentStatus] || [];
  };

  const validStatuses = getValidNextStatuses(order.status);
  const canUpdate = validStatuses.length > 0;

  const handleStatusUpdate = async () => {
    if (selectedStatus === order.status) {
      return;
    }

    if (!validStatuses.includes(selectedStatus)) {
      setError("هذا الانتقال غير مسموح به");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await adminOrderService.updateStatus(order.id, {
        status: selectedStatus,
      });
      onStatusUpdate();
    } catch (err: any) {
      setError(err.message || "فشل تحديث حالة الطلب");
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: Order["status"], isUnconfirm: boolean = false) => {
    const labels: Record<Order["status"], string> = {
      new: isUnconfirm ? "إلغاء التأكيد (جديد)" : "جديد",
      confirmed: "مؤكد",
      preparing: "قيد التحضير",
      shipped: "تم الشحن",
      canceled: "ملغي",
    };
    return labels[status] || status;
  };

  if (!canUpdate) {
    return (
      <div className="text-center py-4 text-slate-500 dark:text-slate-400">
        <p>لا يمكن تغيير حالة هذا الطلب</p>
        <p className="text-sm mt-1">
          الحالة الحالية: {getStatusLabel(order.status)}
        </p>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
        تحديث الحالة
      </label>
      <div className="space-y-3">
        <select
          value={selectedStatus}
          onChange={(e) =>
            setSelectedStatus(e.target.value as Order["status"])
          }
          className="w-full rounded-lg border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-[#0d171b] dark:text-white h-10 px-3"
        >
          <option value={order.status}>
            {getStatusLabel(order.status)} (الحالية)
          </option>
          {validStatuses.map((status) => (
            <option key={status} value={status}>
              {getStatusLabel(status, order.status === "confirmed" && status === "new")}
            </option>
          ))}
        </select>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <button
          onClick={handleStatusUpdate}
          disabled={loading || selectedStatus === order.status}
          className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {loading ? "جاري التحديث..." : "تحديث الحالة"}
        </button>

        <div className="text-xs text-slate-500 dark:text-slate-400">
          <p>الحالات المسموحة:</p>
          <ul className="list-disc list-inside mt-1">
            {validStatuses.map((status) => (
              <li key={status}>{getStatusLabel(status)}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OrderStatusUpdate;

